/** Product Details Tolstoy rail: deferred widget loader, hydration state, and cart bridge. */

import { cart } from '@theme/cart';

const WIDGET_URL = 'https://widget.gotolstoy.com/we/widget.js';
const ROOT_MARGIN = '50% 0px';
const IDLE_TIMEOUT_MS = 1000;
const HYDRATION_TIMEOUT_MS = 15000;
const TILE_SELECTOR = '[data-tolstoy-element^="tolstoy-stories-tile-"]';
const PENDING_POSTER_SELECTOR = '[data-tolstoy-story-poster]';
const CONSENT_FEATURES = [{ name: 'consent-tracking-api', version: '0.1' }];
const bridgeKey = '__dieuxTolstoyCartBridgeBound';

const pageLoaded =
  document.readyState === 'complete'
    ? Promise.resolve()
    : new Promise((resolve) => window.addEventListener('load', resolve, { once: true }));

/** @type {Promise<void> | undefined} */
let widgetRequest;

/**
 * @param {HTMLImageElement} image
 * @returns {Promise<void>}
 */
function imageSettled(image) {
  if (image.complete) return Promise.resolve();
  return new Promise((resolve) => {
    image.addEventListener('load', () => resolve(), { once: true });
    image.addEventListener('error', () => resolve(), { once: true });
  });
}

/** Resolves once the page's priority media has loaded, or at window load when there is none. */
function priorityMediaLoaded() {
  const images = [...document.querySelectorAll('img[fetchpriority="high"]')];
  if (images.length === 0) return pageLoaded;
  return Promise.race([pageLoaded, Promise.all(images.map(imageSettled))]);
}

function afterIdle() {
  return new Promise((resolve) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => resolve(), { timeout: IDLE_TIMEOUT_MS });
    } else {
      window.setTimeout(resolve, 200);
    }
  });
}

function marketingAllowed() {
  try {
    return window.Shopify?.customerPrivacy?.marketingAllowed?.() === true;
  } catch {
    return false;
  }
}

/** Resolves once Shopify's customer-privacy API can answer, or at once when it cannot load. */
function consentApiReady() {
  if (typeof window.Shopify?.customerPrivacy?.marketingAllowed === 'function') return Promise.resolve();
  return new Promise((resolve) => {
    try {
      if (typeof window.Shopify?.loadFeatures !== 'function') {
        resolve();
        return;
      }
      window.Shopify.loadFeatures(CONSENT_FEATURES, () => resolve());
    } catch {
      resolve();
    }
  });
}

function bindTolstoyCartRefresh() {
  if (window[bridgeKey]) return;

  const widget = window.tolstoyWidget;
  if (!widget || typeof widget.subscribe !== 'function') return;

  window[bridgeKey] = true;
  widget.subscribe(
    'tolstoy_add_to_cart',
    (data) => {
      if (!data || data.success === false || typeof data.error !== 'undefined') return;

      window.setTimeout(async () => {
        const refreshed = await cart?.refresh?.();
        if (refreshed) window.tolstoyWidget?.closePlayer?.();
      }, 1000);
    },
    {},
  );
}

/**
 * Injects the Tolstoy stories widget once per document.
 * @param {DOMStringMap} config
 * @returns {Promise<void>}
 */
function loadWidget(config) {
  widgetRequest ??= new Promise((resolve, reject) => {
    window.tolstoyCurrencySymbol ??= config.currencySymbol;
    window.tolstoyMoneyFormat ??= config.moneyFormat;
    window.addEventListener('tolstoyWidgetReady', bindTolstoyCartRefresh, { once: true });

    const script = document.createElement('script');
    script.type = 'module';
    script.src = WIDGET_URL;
    script.dataset.shop = config.shop ?? '';
    script.dataset.appKey = config.appKey ?? '';
    script.dataset.productId = config.productId ?? '';
    script.dataset.templateName = config.templateName ?? '';
    script.addEventListener(
      'load',
      () => {
        bindTolstoyCartRefresh();
        resolve();
      },
      { once: true },
    );
    script.addEventListener('error', () => reject(new Error('Tolstoy widget failed to load')), { once: true });
    document.head.append(script);
  });

  return widgetRequest;
}

class DieuxTolstoyUgc extends HTMLElement {
  /** @type {AbortController | undefined} */
  #lifecycle;
  /** @type {MutationObserver | undefined} */
  #statusObserver;
  /** @type {IntersectionObserver | undefined} */
  #viewportObserver;
  /** @type {number | undefined} */
  #timeout;
  #rehydrated = false;

  connectedCallback() {
    const stories = this.querySelector('tolstoy-stories');
    if (!stories || this.hasAttribute('data-visual-preview') || this.#settle(stories)) return;

    this.#lifecycle = new AbortController();
    this.#watch(stories);
    this.#schedule(this.#lifecycle.signal);
  }

  disconnectedCallback() {
    this.#lifecycle?.abort();
    this.#lifecycle = undefined;
    this.#stopWatching();
  }

  /** @param {AbortSignal} signal */
  async #schedule(signal) {
    await priorityMediaLoaded();
    await afterIdle();
    await consentApiReady();
    if (signal.aborted) return;

    if (marketingAllowed()) {
      this.#observeViewport();
      return;
    }

    this.#collapse();
    const waiting = new AbortController();
    signal.addEventListener('abort', () => waiting.abort(), { once: true });
    document.addEventListener(
      'visitorConsentCollected',
      () => {
        if (!marketingAllowed()) return;
        waiting.abort();
        this.#reveal();
      },
      { signal: waiting.signal },
    );
  }

  #reveal() {
    delete this.dataset.state;
    this.hidden = false;
    this.#observeViewport();
  }

  #observeViewport() {
    this.#viewportObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#viewportObserver?.disconnect();
        this.#viewportObserver = undefined;
        this.#start();
      },
      { rootMargin: ROOT_MARGIN },
    );
    this.#viewportObserver.observe(this);
  }

  #start() {
    this.#timeout = window.setTimeout(() => this.#collapse(), HYDRATION_TIMEOUT_MS);
    loadWidget(this.dataset).catch(() => this.#collapse());
  }

  /** @param {Element} stories */
  #watch(stories) {
    this.#statusObserver?.disconnect();
    this.#statusObserver = new MutationObserver(() => this.#settle(stories));
    this.#statusObserver.observe(stories, { attributes: true, attributeFilter: ['data-status'] });
  }

  /**
   * @param {Element} stories
   * @returns {boolean} Whether the rail reached a final state.
   */
  #settle(stories) {
    const status = stories.getAttribute('data-status');
    if (status === 'loaded') {
      this.#stopWatching();
      this.hidden = false;
      this.dataset.state = 'ready';
      this.#confirmPosters(stories);
      return true;
    }
    if (status && status !== 'loading') {
      this.#stopWatching();
      this.#collapse();
      return true;
    }
    return false;
  }

  /**
   * Rebuilds a rail whose first tile never received its poster once the vendor had a painted frame to set it.
   * @param {Element} stories
   */
  #confirmPosters(stories) {
    if (this.#rehydrated || !stories.querySelector(`${TILE_SELECTOR} ${PENDING_POSTER_SELECTOR}`)) return;

    window.DieuxFrame.afterPaint(() => {
      if (!this.isConnected || !stories.isConnected || this.#rehydrated) return;
      if (!stories.querySelector(TILE_SELECTOR)?.querySelector(PENDING_POSTER_SELECTOR)) return;
      this.#rehydrate(stories);
    });
  }

  /** @param {Element} stories */
  #rehydrate(stories) {
    this.#rehydrated = true;
    const fresh = /** @type {Element} */ (stories.cloneNode(false));
    fresh.removeAttribute('data-status');
    delete this.dataset.state;
    this.#watch(fresh);
    this.#timeout = window.setTimeout(() => this.#collapse(), HYDRATION_TIMEOUT_MS);
    stories.replaceWith(fresh);
  }

  #collapse() {
    if (this.dataset.state === 'ready') return;
    window.clearTimeout(this.#timeout);
    this.dataset.state = 'empty';
    this.hidden = true;
  }

  #stopWatching() {
    window.clearTimeout(this.#timeout);
    this.#timeout = undefined;
    this.#statusObserver?.disconnect();
    this.#statusObserver = undefined;
    this.#viewportObserver?.disconnect();
    this.#viewportObserver = undefined;
  }
}

if (!customElements.get('dieux-tolstoy-ugc')) {
  customElements.define('dieux-tolstoy-ugc', DieuxTolstoyUgc);
}
